import React, { useState, useEffect } from "react";
import LayoutAdmin from "../components/layout/LayoutAdmin";
import { useAuth } from "../context/authContext";
import { FiArrowRight, FiSearch } from "react-icons/fi";
import apiService from "../actions/apiService";

// Helper function to format relative time (e.g., "about 3 hours ago")
const formatRelativeTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return diffInMinutes === 1
      ? "about 1 minute ago"
      : `about ${diffInMinutes} minutes ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return diffInHours === 1
      ? "about 1 hour ago"
      : `about ${diffInHours} hours ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return diffInDays === 1
      ? "about 1 day ago"
      : `about ${diffInDays} days ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return diffInWeeks === 1
      ? "about 1 week ago"
      : `about ${diffInWeeks} weeks ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return diffInMonths === 1
      ? "about 1 month ago"
      : `about ${diffInMonths} months ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return diffInYears === 1
    ? "about 1 year ago"
    : `about ${diffInYears} years ago`;
};

// Helper function to get admin user display name
const getAdminDisplayName = (user) => {
  if (!user) return "Admin user";
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  if (user.email) {
    return user.email.split("@")[0];
  }
  return "Admin user";
};

const StatCard = ({ title, metric, linkLabel, linkHref = "" }) => {
  return (
    <div className="bg-white w-full border min-w-[320px] lg:min-w-full border-[#DCEDFF] rounded-[12px] p-[20px] flex flex-col h-full">
      <div className="flex gap-2 flex-wrap items-start justify-between mb-0">
        <div>
          <p className="text-tag-d text-esdarkGray">{title}</p>
        </div>
        <a
          href={linkHref}
          className="text-tag-d text-esblue font-medium flex items-center gap-2"
        >
          {linkLabel}
          <FiArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>
      <div className="mt-auto pt-8 text-big-price md:text-big-price-d font-bold text-esblue">
        {metric}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [recentLogs, setRecentLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    activeTransactions: 0,
    totalRevenue: 0,
    pendingReviews: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [pendingTxLoading, setPendingTxLoading] = useState(true);
  const [releasingFunds, setReleasingFunds] = useState(null);

  useEffect(() => {
    const fetchRecentLogs = async () => {
      try {
        setLogsLoading(true);
        const response = await apiService.get("/admin/action-logs", {
          page: 1,
          limit: 3,
        });

        if (response.success) {
          setRecentLogs(response.data?.notifications || []);
        }
      } catch (err) {
        console.error("Error fetching recent admin logs:", err);
      } finally {
        setLogsLoading(false);
      }
    };

    fetchRecentLogs();
  }, []);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setStatsLoading(true);
        const response = await apiService.get("/admin/dashboard-metrics");

        if (response.success) {
          setDashboardStats(response.data || {});
        }
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  useEffect(() => {
    const fetchPendingTransactions = async () => {
      try {
        setPendingTxLoading(true);
        const response = await apiService.get("/admin/transactions", {
          status: "confirmed",
          limit: 5,
          page: 1,
        });

        if (response.success) {
          // Filter transactions where both buyer and seller confirmed, and funds not yet released
          const pending = (response.data?.transactions || []).filter(
            (tx) =>
              tx.buyer_confirmed &&
              tx.seller_confirmed &&
              !tx.admin_released &&
              tx.status === "confirmed"
          );
          setPendingTransactions(pending.slice(0, 3));
        }
      } catch (err) {
        console.error("Error fetching pending transactions:", err);
      } finally {
        setPendingTxLoading(false);
      }
    };

    fetchPendingTransactions();
  }, []);

  const handleReleaseFunds = async (transaction) => {
    setReleasingFunds(transaction.id);
    try {
      const res = await apiService.post(
        `/admin/transactions/${transaction.id}/release`
      );
      if (res?.success) {
        // Remove the transaction from the list
        setPendingTransactions((prev) =>
          prev.filter((tx) => tx.id !== transaction.id)
        );
        // Refresh dashboard stats
        const statsRes = await apiService.get("/admin/dashboard-metrics");
        if (statsRes?.success) {
          setDashboardStats(statsRes.data || {});
        }
        setReleasingFunds(null);
      } else {
        setReleasingFunds(null);
      }
    } catch (err) {
      console.error("Error releasing funds:", err);
      setReleasingFunds(null);
    }
  };

  return (
    <LayoutAdmin>
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative z-[10]">
        <div className="mb-8 px-5">
          <h2 className="md:text-page-title-d text-page-title !font-[600] text-white lg:text-[#1E1E1E] font-rubik">
            Welcome back, {user?.firstName} {user?.lastName}{" "}
            <span role="img" aria-label="waving hand">
              👋
            </span>
          </h2>
          <p className="text-small-price-d font-normal opacity-50 text-white lg:text-[#1E1E1E] mt-4">
            Manage your platform efficiently
          </p>
        </div>

        <div className="px-5 lg:px-0 lg:ps-5">
          <div className="mb-8 ms-0 md:ms-5 text-big-price md:text-big-price-d !font-medium text-[#fff] lg:text-esdarkGray font-rubik">
            General overview
          </div>
          <div className="flex overflow-y-auto lg:grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              title="Total users"
              metric={
                <span>{statsLoading ? 0 : dashboardStats.totalUsers || 0}</span>
              }
              linkLabel="Manage users"
              linkHref="/admin/users"
            />
            <StatCard
              title="Active transactions"
              metric={
                <span>
                  {statsLoading ? 0 : dashboardStats.activeTransactions || 0}
                </span>
              }
              linkLabel="See transactions"
              linkHref="/admin/transactions"
            />
            <StatCard
              title="Total revenue"
              metric={
                <span>
                  {statsLoading ? "0" : `${dashboardStats.totalRevenue || 0}`} €
                </span>
              }
              linkLabel="See transactions"
              linkHref="/admin/transactions"
            />
            <StatCard
              title="Pending reviews"
              metric={
                <span>
                  {statsLoading ? 0 : dashboardStats.pendingReviews || 0}
                </span>
              }
              linkLabel="See transactions"
              linkHref="/admin/transactions"
            />
          </div>
        </div>

        {/* Waiting for actions + Recent activity */}
        <div className="mt-8 grid grid-cols-1 px-5 lg:px-0 lg:ps-5 lg:grid-cols-3 gap-8 items-start">
          {/* Left: Waiting for actions (spans 2) */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <div className="flex flex-wrap items-center justify-between mb-8">
                <h4 className="md:text-big-price-d !font-[500] text-big-price font-rubik text-esdarkGray">
                  Waiting for actions
                </h4>
              </div>
              <div className="divide-y divide-[#1E1E1E] divide-opacity-25">
                {pendingTxLoading ? (
                  <div className="py-4 text-center text-gray-500 text-sm">
                    Loading...
                  </div>
                ) : pendingTransactions.length === 0 ? (
                  <div className="py-4 text-center text-gray-500 text-sm">
                    No transactions awaiting fund release
                  </div>
                ) : (
                  pendingTransactions.map((transaction) => (
                    <div key={transaction.id} className="py-4">
                      <div className="flex flex-wrap items-center gap-8">
                        <div className="text-[16px] font-[400] opacity-50 text-[#000025] min-w-[170px]">
                          {transaction.item?.reference_code
                            ? `#${transaction.item.reference_code}`
                            : `N/A`}
                        </div>
                        <div className="flex items-center flex-wrap gap-4">
                          <div className="w-[50px] h-[41.981px] rounded-md overflow-hidden bg-slate-200 flex-shrink-0">
                            {transaction.item?.images?.[0]?.file_url ? (
                              <img
                                src={transaction.item.images[0].file_url}
                                alt={transaction.item.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[16px] font-[600] text-esDarkBlue lg:truncate">
                              {transaction.item?.title || "N/A"}
                            </p>
                            <p className="text-[14px] font-[600] opacity-50 text-[#000025]">
                              {transaction.item?.category || "N/A"}
                            </p>
                          </div>
                        </div>
                        <div className="text-[#000025] font-bold text-[18px]">
                          {transaction.buyer_paid_amount || 0}€
                        </div>
                        <button
                          type="button"
                          onClick={() => handleReleaseFunds(transaction)}
                          disabled={releasingFunds === transaction.id}
                          className="ml-auto inline-flex items-center gap-4 bg-[#B9FFCC] text-[#16653F] text-[14px] px-[12px] py-[9px] rounded-[8px] font-[600] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="inline-flex items-center justify-center ">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M20 6L9 17L4 12"
                                stroke="#16653F"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          {releasingFunds === transaction.id
                            ? "Releasing..."
                            : "Release funds"}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {/* Bottom single activity line */}
              {/* <div className="pt-4">
                <div className="flex justify-between items-center gap-2 flex-wrap">
                  <div className="">
                    <div className="flex items-start gap-2">
                      <span className="text-esblue">
                        <FiSearch className="w-6 h-6" />
                      </span>
                      <div>
                        <p className="text-[14px] font-[600] opacity-75 text-esDarkBlue">
                          Released funds for TX001
                        </p>
                      </div>
                    </div>
                    <p className="text-[16px] opacity-50 mt-2 text-esGray">
                      by Admin user
                    </p>
                  </div>
                  <div className="text-[12px] font-[500] text-[#000025] opacity-50 whitespace-nowrap">
                    about 3 hours ago
                  </div>
                </div>
              </div> */}
              <div className="pt-8 text-right">
                <a
                  href="/admin/transactions"
                  className="inline-flex items-center gap-1 text-[#2A8BF7] text-[14px] px-[12px] py-[12px] rounded-[8px] font-[600] bg-[#F5F8FF]"
                >
                  See all transactions
                  <FiArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>

          {/* Right: Recent activity */}
          <div>
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h4 className="md:text-big-price-d !font-[500] text-big-price font-rubik text-esdarkGray mb-8">
                Recent activity
              </h4>
              <div className="divide-y">
                {logsLoading ? (
                  <div className="py-3 text-center text-gray-500 text-sm">
                    Loading...
                  </div>
                ) : recentLogs.length === 0 ? (
                  <div className="py-3 text-center text-gray-500 text-sm">
                    No recent activity
                  </div>
                ) : (
                  recentLogs.map((notification) => (
                    <div key={notification.id} className="py-3">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="">
                          <div className="flex items-start gap-2">
                            <span className="text-esblue">
                              <FiSearch className="w-6 h-6" />
                            </span>
                            <div>
                              <p className="text-[16px] font-[600] text-[#1E1E1E] opacity-75">
                                {notification.title}
                              </p>
                            </div>
                          </div>
                          <p className="text-[16px] opacity-50 mt-2 text-[#000025] font-[400]">
                            by {getAdminDisplayName(notification.user)}
                          </p>
                        </div>
                        <div className="text-[12px] font-[500] text-[#000025] opacity-50 whitespace-nowrap">
                          {formatRelativeTime(notification.created_at)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutAdmin>
  );
};

export default AdminDashboard;
